import React, { useState, useRef, useCallback } from 'react';
import { Bold, Italic, Underline, Paperclip } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { toast } from 'sonner';

export default function AddNoteSheet({ isOpen, onClose, onSave, saving }) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachments, setAttachments] = useState([]);

  const execCmd = useCallback((cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  }, []);

  const handleAttachment = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) {
      setAttachments(prev => [...prev, ...files]);
      toast.success(`${files.length} file(s) attached`);
    }
    e.target.value = '';
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = () => {
    const html = editorRef.current?.innerHTML || '';
    const text = editorRef.current?.innerText?.trim() || '';
    if (!text) { toast.error('Note cannot be empty'); return; }

    onSave(html, attachments);

    // Reset
    if (editorRef.current) editorRef.current.innerHTML = '';
    setAttachments([]);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-md flex flex-col" data-testid="note-sheet">
        <SheetHeader>
          <SheetTitle>Add Note</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 mt-4 flex-1 min-h-0">
          {/* Label */}
          <Label className="text-sm font-medium">Note</Label>

          {/* Rich Text Editor */}
          <div className="border rounded-md overflow-hidden flex-1 flex flex-col" data-testid="rich-note-editor">
            {/* Toolbar */}
            <div className="flex items-center gap-0.5 px-2 py-1.5 border-b bg-muted/30" data-testid="note-toolbar">
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-colors"
                onClick={() => execCmd('bold')}
                title="Bold"
                data-testid="toolbar-bold"
              >
                <Bold className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-colors"
                onClick={() => execCmd('italic')}
                title="Italic"
                data-testid="toolbar-italic"
              >
                <Italic className="h-4 w-4" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-colors"
                onClick={() => execCmd('underline')}
                title="Underline"
                data-testid="toolbar-underline"
              >
                <Underline className="h-4 w-4" />
              </button>
              <div className="w-px h-5 bg-border mx-1" />
              <button
                type="button"
                className="p-1.5 rounded hover:bg-muted transition-colors"
                onClick={() => fileInputRef.current?.click()}
                title="Attach file"
                data-testid="toolbar-attach"
              >
                <Paperclip className="h-4 w-4" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleAttachment}
              />
            </div>

            {/* Editable area */}
            <div
              ref={editorRef}
              contentEditable
              className="flex-1 px-3 py-2 min-h-[140px] max-h-[280px] overflow-y-auto text-sm focus:outline-none [&_b]:font-bold [&_i]:italic [&_u]:underline"
              data-placeholder="Write your note here..."
              data-testid="note-content-editable"
              suppressContentEditableWarning
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            />
          </div>

          {/* Attachment chips */}
          {attachments.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="attachment-list">
              {attachments.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2.5 py-1">
                  <Paperclip className="h-3 w-3" />
                  {f.name.length > 20 ? f.name.slice(0, 18) + '...' : f.name}
                  <button type="button" onClick={() => removeAttachment(i)} className="ml-0.5 hover:text-red-500 font-bold">&times;</button>
                </span>
              ))}
            </div>
          )}

          {/* Save Button */}
          <Button
            className="w-full"
            onClick={handleSave}
            disabled={saving}
            data-testid="note-save-btn"
          >
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
